import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import DeleteIconButton from '@shared/components/core/button/DeleteIconButton';
import HorizontalFieldContainer from '@shared/components/core/field/HorizontalFieldContainer';
import { StickyFooter } from '@shared/components/core/structure/CommonBodyLayoutStyles';
import DictationControlField from '@shared/components/input/field/DictationControlField';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

export default function ProfileDictationPageContent() {
    const { t } = useTranslation();
    const { organizationId } = useParams();
    const initialDictationCommands = useEditProfileStore((state) => state.dictationCommands);
    const hasChanges = useEditProfileStore((state) => state.hasChanges);
    const id = useEditProfileStore((state) => state.id);
    const isPreview = useEditProfileStore((state) => state.isPreview);
    const [dictationCommands, setDictationCommands] = useState<string[]>(initialDictationCommands || []);

    useEffect(() => {
        if (initialDictationCommands) {
            setDictationCommands([...initialDictationCommands]);
        }
    }, [initialDictationCommands]);

    const handleCommandChange = (value: string, index: number) => {
        dictationCommands[index] = value;
        setDictationCommands([...dictationCommands]);
        useEditProfileStore.getState().setHasChanges(true);
    };
    const handleRemoveDictationCommand = (index: number) => {
        dictationCommands.splice(index, 1);
        setDictationCommands([...dictationCommands]);
        useEditProfileStore.getState().setHasChanges(true);
    };
    const handleAddDictationCommand = () => {
        dictationCommands.unshift('');
        setDictationCommands([...dictationCommands]);
        useEditProfileStore.getState().setHasChanges(true);
    };

    const handleSave = async () => {
        const filteredCommands = dictationCommands.filter((command) => command.length > 0);
        const editProfileStore = useEditProfileStore.getState();
        editProfileStore.updateDictationCommands(filteredCommands);
        if (organizationId) {
            editProfileStore.setOrganizationId(organizationId);
        }
        await editProfileStore.saveChanges();
    };
    return (
        <>
            <AddHeaderContainer>
                <Button $color="solidAccent" $dashed startIcon="plus" onClick={() => handleAddDictationCommand()}>
                    {t('common:dictation.add')}
                </Button>
            </AddHeaderContainer>

            {dictationCommands?.map((h, i) => (
                <HorizontalFieldContainer key={i}>
                    <DictationControlField value={h} onChange={(e) => handleCommandChange(e, i)} disabled={isPreview} />
                    <DeleteIconButton disabled={isPreview} onClick={() => handleRemoveDictationCommand(i)}>
                        {t('common:dictation.remove')}
                    </DeleteIconButton>
                </HorizontalFieldContainer>
            ))}

            {hasChanges && (
                <UnsavedChangesDialog
                    hasChanges={hasChanges}
                    setHasChanges={useEditProfileStore.getState().setHasChanges}
                    onSave={handleSave}
                />
            )}
            <StickyFooter contentPosition="center" $visuallyHide={!hasChanges && id !== undefined}>
                <Button $color="primary" $size="large" startIcon="save" onClick={handleSave}>
                    {t('common:saveAndFinish')}
                </Button>
            </StickyFooter>
        </>
    );
}

const AddHeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1rem;
    > * {
        margin: 0.25rem;
    }
`;
